import { CheckCircleOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'
import { Link } from 'react-router-dom'
import { routes } from '../../../../../constants/routes'
import { useGetShiftSchedulesQuery } from '../../../../../store/api/shift-schedules-api'
import ShiftSchedule from '../../../../../types/api/shift-schedule'
import { BusinessRole } from '../../../../../types/api/user'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'

const getTodayShiftSchedulesQueryParams = (): EndpointQueryParameters => ({
    fields: ['documentId', 'date'],
    filters: {
        date: {
            $eq: dayjs().format('YYYY-MM-DD'),
        },
        employee: {
            user_position: {
                name: {
                    $eq: BusinessRole.SecondPrinter,
                },
            },
        },
    },
    populate: {
        shiftType: {
            fields: ['documentId', 'name', 'code'],
        },
    },
    pagination: {
        page: 1,
        pageSize: 20,
    },
})

const normalizeShiftTypeValue = (value?: string) => value?.toLowerCase() || ''

const isDayShift = (shiftSchedule: ShiftSchedule) => {
    const code = normalizeShiftTypeValue(shiftSchedule.shiftType?.code)
    const name = normalizeShiftTypeValue(shiftSchedule.shiftType?.name)

    return code === 'd' || code.includes('day') || name.includes('день') || name.includes('днев')
}

const isNightShift = (shiftSchedule: ShiftSchedule) => {
    const code = normalizeShiftTypeValue(shiftSchedule.shiftType?.code)
    const name = normalizeShiftTypeValue(shiftSchedule.shiftType?.name)

    return code === 'n' || code.includes('night') || name.includes('ноч')
}

const getShiftScheduleDateKey = (shiftSchedule: ShiftSchedule) => shiftSchedule.date

const REQUIRED_SHIFTS_COUNT = 3

const hasFilledDayAndNightOnSameDate = (shiftSchedules: ShiftSchedule[]) => {
    const shiftsByDate = shiftSchedules.reduce<
        Record<string, { dayCount: number; nightCount: number }>
    >((acc, shiftSchedule) => {
        const dateKey = getShiftScheduleDateKey(shiftSchedule)

        if (!acc[dateKey]) {
            acc[dateKey] = { dayCount: 0, nightCount: 0 }
        }

        if (isDayShift(shiftSchedule)) acc[dateKey].dayCount += 1
        if (isNightShift(shiftSchedule)) acc[dateKey].nightCount += 1

        return acc
    }, {})

    return Object.values(shiftsByDate).some(
        ({ dayCount, nightCount }) =>
            dayCount === REQUIRED_SHIFTS_COUNT && nightCount === REQUIRED_SHIFTS_COUNT
    )
}

const ShiftsButton: FC = () => {
    const { data, isLoading } = useGetShiftSchedulesQuery(getTodayShiftSchedulesQueryParams())
    const shifts = data?.data || []
    const shiftsAreFilled = hasFilledDayAndNightOnSameDate(shifts)

    return (
        <Link to={routes.shifts}>
            <Button type={'link'} loading={isLoading}>
                {shiftsAreFilled ? (
                    <Typography.Text type='success'>
                        <CheckCircleOutlined /> Смены заполнены
                    </Typography.Text>
                ) : (
                    'Заполнить смены'
                )}
            </Button>
        </Link>
    )
}

export default ShiftsButton
